'use client';

import { use, useState, useMemo } from 'react';
import {
    ChevronRight,
    GraduationCap,
    ArrowLeft,
    Download,
    Mail,
    Phone,
    MapPin,
    Calendar,
    User,
    ShieldCheck,
    CreditCard,
    History,
    Eye,
    Receipt,
    X,
    Printer,
    Share2,
    RotateCw,
    TrendingUp,
    FileText,
    MoreVertical,
    CheckCircle2,
    AlertCircle,
    Clock,
    UploadCloud
} from 'lucide-react';
import Link from 'next/link';
import { notFound } from 'next/navigation';
import {
    getStudentData,
    getSchoolName,
    SchoolUnit,
    Student,
    PaymentHistory
} from '@/lib/data';

export default function StudentDetailPage({ params }: { params: Promise<{ unit: string; id: string }> }) {
    const { unit, id } = use(params);
    const validUnits: SchoolUnit[] = ['paud', 'paud-pekanbaru', 'paud-kampar', 'paud-siak', 'sd', 'smp', 'smk'];

    if (!validUnits.includes(unit as SchoolUnit)) {
        notFound();
    }

    const schoolUnit = unit as SchoolUnit;
    const student = getStudentData(schoolUnit, id);
    const schoolName = getSchoolName(schoolUnit);

    const [selectedTransaction, setSelectedTransaction] = useState<PaymentHistory | null>(null);

    if (!student) {
        notFound();
    }

    return (
        <main className="p-4 md:p-10 space-y-10 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Navigasi / Breadcrumbs */}
            <nav className="flex items-center gap-2 text-muted text-[10px] font-bold uppercase tracking-widest">
                <Link href="/yayasan" className="hover:text-primary transition-colors">Yayasan</Link>
                <ChevronRight className="w-3 h-3" />
                <Link href={`/yayasan/${unit}`} className="hover:text-primary transition-colors">{schoolName}</Link>
                <ChevronRight className="w-3 h-3" />
                <Link href={`/yayasan/${unit}/siswa`} className="hover:text-primary transition-colors">Data Siswa</Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-foreground">{student.name}</span>
            </nav>

            {/* Profil Hero Section */}
            <section className="bg-surface rounded-4xl border border-border-main shadow-md overflow-hidden relative group">

                <div className="relative z-10 p-8 md:p-12">
                    <div className="flex flex-col lg:flex-row gap-10 items-start">
                        {/* Avatar & Info Dasar */}
                        <div className="flex flex-col items-center text-center space-y-4 shrink-0">
                            <div className="w-32 h-32 md:w-40 md:h-40 rounded-4xl bg-primary-soft border border-primary/10 p-1">
                                <div className="w-full h-full rounded-[1.75rem] bg-surface flex items-center justify-center text-primary relative overflow-hidden shadow-inner">
                                    <span className="absolute inset-0 flex items-center justify-center text-4xl md:text-5xl font-bold">
                                        {student.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                    </span>
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className={`px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border ${student.status === 'active' ? 'bg-success-soft text-success border-success/10' : 'bg-muted-soft text-muted border-muted/10'
                                    }`}>
                                    Status {student.status === 'active' ? 'Aktif' : 'Non-Aktif'}
                                </span>
                                <p className="text-[10px] font-bold text-muted uppercase tracking-[0.2em]">NISN: {student.id}</p>
                            </div>
                        </div>

                        {/* Detail Identitas */}
                        <div className="flex-1 space-y-8 w-full">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                <div className="space-y-1">
                                    <h1 className="text-3xl md:text-5xl font-bold text-foreground tracking-tight">{student.name}</h1>
                                    <div className="flex items-center gap-3 text-secondary font-medium">
                                        <GraduationCap className="w-5 h-5 text-primary" />
                                        <span>{student.class}</span>
                                        <span className="w-1.5 h-1.5 rounded-full bg-border-main"></span>
                                        <span>TA 2024/2025</span>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <button className="p-3 bg-surface border border-border-main rounded-2xl text-muted hover:text-primary hover:border-primary transition-all shadow-sm">
                                        <Printer className="w-5 h-5" />
                                    </button>
                                    <button className="px-6 py-3 bg-primary text-white rounded-2xl text-xs font-bold uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all flex items-center gap-2">
                                        <Download className="w-4 h-4" /> Ekspor Profil
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-8 border-t border-border-main/50">
                                <div className="space-y-4">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest flex items-center gap-2">
                                        <ShieldCheck className="w-3.5 h-3.5" /> Informasi Orang Tua
                                    </p>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 text-sm font-bold text-foreground">
                                            <div className="w-8 h-8 rounded-lg bg-background flex items-center justify-center text-primary"><User className="w-4 h-4" /></div>
                                            {student.parentName || '-'}
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-secondary">
                                            <div className="w-8 h-8 rounded-lg bg-background flex items-center justify-center text-primary"><Mail className="w-4 h-4" /></div>
                                            {student.parentName?.toLowerCase().replace(' ', '.')}@gmail.com
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-secondary">
                                            <div className="w-8 h-8 rounded-lg bg-background flex items-center justify-center text-primary"><Phone className="w-4 h-4" /></div>
                                            +62 812-xxxx-7890
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest flex items-center gap-2">
                                        <TrendingUp className="w-3.5 h-3.5" /> Ringkasan Keuangan
                                    </p>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center p-3 bg-background rounded-2xl border border-border-main">
                                            <span className="text-[10px] font-bold text-muted uppercase tracking-wider">Total Tagihan</span>
                                            <span className="text-sm font-bold text-foreground tabular-nums">{student.totalBill}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-3 bg-error-soft/30 rounded-2xl border border-error/10">
                                            <span className="text-[10px] font-bold text-error uppercase tracking-wider">Sisa Piutang</span>
                                            <span className="text-sm font-bold text-error tabular-nums">{student.receivable}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest flex items-center gap-2">
                                        <Calendar className="w-3.5 h-3.5" /> Tanggal Registrasi
                                    </p>
                                    <div className="p-4 bg-background rounded-2xl border border-border-main flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-info-soft text-info flex items-center justify-center">
                                            <Calendar className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-foreground">12 Juli 2024</p>
                                            <p className="text-[10px] text-muted font-bold uppercase tracking-wider">Semester Ganjil</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Riwayat Transaksi Section Header */}
            <section className="space-y-6">
                <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                        <h2 className="text-lg font-bold text-[#1a2e1a] flex items-center gap-2">
                            <History className="w-5 h-5 text-[#15803d]" /> Riwayat Pembayaran
                        </h2>
                        <p className="text-[#64748b] text-xs">Daftar transaksi pembayaran SPP dan biaya penunjang lainnya</p>
                    </div>
                    <button className="px-5 py-2.5 bg-white border border-[#e2e8f0] rounded-full text-[9px] font-bold uppercase tracking-[0.15em] text-[#475569] flex items-center gap-2 hover:bg-[#f8fafc] transition-all">
                        <RotateCw className="w-3.5 h-3.5" /> PERBARUI DATA
                    </button>
                </div>

                <div className="bg-surface rounded-3xl border border-border-main shadow-sm overflow-hidden border-b-4 border-b-primary/10">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-background/50 border-b border-border-main">
                                    <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Kwitansi / Tanggal</th>
                                    <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Jenis / Kategori</th>
                                    <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Metode</th>
                                    <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Jumlah</th>
                                    <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Status</th>
                                    <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Aksi</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border-main">
                                {student.paymentHistory && student.paymentHistory.length > 0 ? (
                                    student.paymentHistory.map((tx) => (
                                        <tr key={tx.id} className="hover:bg-primary-soft/5 transition-colors group/tr">
                                            <td className="px-8 py-5 whitespace-nowrap">
                                                <p className="text-sm font-bold text-foreground group-hover/tr:text-primary transition-colors">{tx.receiptNo}</p>
                                                <p className="text-[10px] font-bold text-muted uppercase tracking-wider tabular-nums">{tx.date}</p>
                                            </td>
                                            <td className="px-8 py-5 whitespace-nowrap">
                                                <p className="text-xs font-bold text-foreground">{tx.category || 'Biaya Sekolah'}</p>
                                            </td>
                                            <td className="px-8 py-5 whitespace-nowrap">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-8 h-8 rounded-lg bg-background flex items-center justify-center text-primary border border-border-main group-hover/tr:border-primary/20">
                                                        <CreditCard className="w-4 h-4" />
                                                    </div>
                                                    <span className="text-[10px] font-bold text-foreground uppercase tracking-wider">{tx.method}</span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-5 whitespace-nowrap">
                                                <p className="text-sm font-bold text-foreground tabular-nums">{tx.amount}</p>
                                            </td>
                                            <td className="px-8 py-5 whitespace-nowrap">
                                                <span className={`px-2.5 py-1 rounded-lg text-[9px] font-bold uppercase tracking-wider border ${tx.status === 'success' ? 'bg-success-soft text-success border-success/10' : 'bg-warning-soft text-warning border-warning/10'
                                                    }`}>
                                                    {tx.status === 'success' ? 'Berhasil' : tx.status === 'pending' ? 'Tertunda' : 'Gagal'}
                                                </span>
                                            </td>
                                            <td className="px-8 py-5 text-right whitespace-nowrap">
                                                <button
                                                    onClick={() => setSelectedTransaction(tx)}
                                                    className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted hover:text-primary hover:border-primary hover:shadow-lg transition-all active:scale-95"
                                                >
                                                    <Eye className="w-5 h-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="px-8 py-20 text-center">
                                            <div className="flex flex-col items-center gap-4 opacity-30">
                                                <Receipt className="w-16 h-16" />
                                                <p className="font-bold text-sm">Belum ada riwayat transaksi</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Detail Transaksi Side Drawer */}
            {selectedTransaction && (
                <div className="fixed inset-0 z-100 flex justify-end animate-in fade-in duration-300">
                    <div className="absolute inset-0 bg-background/40 backdrop-blur-sm" onClick={() => setSelectedTransaction(null)}></div>
                    <div className="relative w-full max-w-xl bg-surface h-full shadow-2xl border-l border-border-main overflow-hidden animate-in slide-in-from-right duration-500 flex flex-col">
                        {/* Drawer Header */}
                        <div className="p-6 md:p-8 flex items-center justify-between border-b border-border-main bg-background/30 mt-10 md:mt-0">
                            <h3 className="text-xl font-bold text-foreground">Detail Transaksi</h3>
                            <button onClick={() => setSelectedTransaction(null)} className="w-10 h-10 rounded-full bg-surface border border-border-main flex items-center justify-center text-muted hover:text-foreground transition-all">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto custom-scrollbar p-8 space-y-8">
                            {/* Kartu Ringkasan Sesuai Gambar */}
                            <div className="p-6 bg-[#f0f9f4] rounded-[2rem] border border-[#e8f5ed] flex items-center justify-between gap-6">
                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                    <div className="w-12 h-12 rounded-2xl bg-[#dcfce7] text-[#15803d] flex items-center justify-center shrink-0">
                                        <TrendingUp className="w-6 h-6 transform rotate-45" />
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                        <p className="text-sm font-bold text-[#1f2937] leading-tight break-words">
                                            Pembayaran untuk {selectedTransaction.category || 'Biaya Sekolah'}
                                        </p>
                                        <p className="text-[10px] font-bold text-[#6b7280] uppercase tracking-widest mt-1">SUCCESS</p>
                                    </div>
                                </div>
                                <span className="text-xl font-bold text-[#1f2937] tabular-nums shrink-0 whitespace-nowrap">
                                    {selectedTransaction.amount}
                                </span>
                            </div>

                            {/* Data Rincian Sesuai Layout Gambar */}
                            <div className="space-y-6">
                                <h4 className="text-[10px] font-bold text-[#8696ab] uppercase tracking-[0.15em] mb-4">DATA DETAIL</h4>
                                <div className="space-y-5 px-1">
                                    {[
                                        { label: 'Transaction ID', value: selectedTransaction.id, color: 'text-[#1f2937]' },
                                        { label: 'Transaction Date', value: `${selectedTransaction.date}, 09:42 AM`, color: 'text-[#1f2937]' },
                                        { label: 'Jenis Pembayaran', value: selectedTransaction.category || 'SPP', color: 'text-[#1f2937]' },
                                        { label: 'Siswa / Penerima', value: student.name, avatar: true },
                                        { label: 'Status', value: 'SUCCESS', badge: true },
                                        { label: 'Amount Paid', value: selectedTransaction.amount, bold: true },
                                        { label: 'Admin Fee', value: 'Rp 0', color: 'text-[#9ca3af]' },
                                    ].map((item, idx) => (
                                        <div key={idx} className="flex justify-between items-center text-sm">
                                            <span className="text-[#8696ab] font-medium">{item.label}</span>
                                            {item.avatar ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded-full bg-[#f0fdf4] text-[#15803d] flex items-center justify-center text-[10px] font-bold border border-[#dcfce7]">
                                                        {item.value.substring(0, 1)}
                                                    </div>
                                                    <span className="font-bold text-[#1f2937]">{item.value}</span>
                                                </div>
                                            ) : item.badge ? (
                                                <div className="flex items-center gap-1.5 px-3 py-1 bg-[#f0fdf4] text-[#22c55e] rounded-full text-[9px] font-bold border border-[#dcfce7]">
                                                    <div className="w-3.5 h-3.5 rounded-full border border-[#22c55e] flex items-center justify-center">
                                                        <CheckCircle2 className="w-2 h-2" />
                                                    </div>
                                                    {item.value}
                                                </div>
                                            ) : (
                                                <span className={`font-bold tabular-nums ${item.color || 'text-[#1f2937]'} ${item.bold ? 'text-lg' : ''}`}>{item.value}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Catatan Sesuai Gambar */}
                            <div className="space-y-3">
                                <h4 className="text-[10px] font-bold text-[#8696ab] uppercase tracking-[0.15em] flex items-center gap-2">
                                    NOTES <FileText className="w-3 h-3 text-[#9ca3af]" />
                                </h4>
                                <div className="p-5 bg-white border border-[#f1f5f9] rounded-2xl">
                                    <p className={`text-xs leading-relaxed ${selectedTransaction.notes ? 'text-[#64748b]' : 'text-muted/50 italic'}`}>
                                        {selectedTransaction.notes || 'Tidak ada catatan untuk transaksi ini.'}
                                    </p>
                                </div>
                            </div>

                            {/* Lampiran */}
                            <div className="space-y-3">
                                <h4 className="text-[10px] font-bold text-[#8696ab] uppercase tracking-[0.15em]">ATTACHMENTS</h4>
                                {selectedTransaction.attachments && selectedTransaction.attachments.length > 0 ? (
                                    <div className="grid grid-cols-2 gap-3">
                                        {selectedTransaction.attachments.map((file, i) => (
                                            <div key={i} className="p-3 border border-[#f1f5f9] rounded-xl bg-white flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-primary" />
                                                <span className="text-[10px] font-bold truncate">Bukti_{i + 1}.pdf</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-8 border-2 border-dashed border-[#f1f5f9] rounded-[2rem] bg-white group transition-all  flex flex-col items-center justify-center gap-2">
                                        <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center text-muted/30">
                                            <UploadCloud className="w-5 h-5" />
                                        </div>
                                        <p className="text-[10px] font-bold text-muted/40 uppercase tracking-widest">Tidak ada lampiran</p>
                                    </div>
                                )}
                            </div>

                            <div className="h-20"></div>
                        </div>

                        {/* Footer Sesuai Gambar */}
                        <div className="p-8 border-t border-[#f1f5f9] flex gap-4 bg-white">
                            <button className="flex-1 py-4.5 bg-white border border-[#e2e8f0] rounded-2xl text-[10px] font-bold uppercase tracking-widest text-[#475569] hover:bg-[#f8fafc] transition-all flex items-center justify-center gap-2 shadow-sm">
                                <Share2 className="w-4 h-4" /> SHARE RECEIPT
                            </button>

                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
